import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import DaumPostcodeEmbed from "react-daum-postcode";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import ConsoleHrozontalHeadTable from "../-components/ConsolehorizontalHeadTable";
import LoadingPop from "components/popup/LoadingPop";
import { ReactComponent as Close } from "assets/morning/icon/close.svg";
import { ReactComponent as ArrowDown } from "assets/morning/icon/arrowDownNavy.svg";
import { toastErrorSyle } from "hooks/utils/util";
import { usePostConsoleWarehouse, usePutConsoleWarehouse } from "hooks/console/useConsoleWarehouse";
import { useGetConsoleDrivers } from "hooks/console/useConsoleDriver";

export type WarehouseFormState = {
    warehouse_no: number | null;
    port_name: string;
    name: string;
    driver_no: string;
    post: string;
    address: string;
    addressDetail: string;
};

export default function WarehouseForm({
    form,
    setForm,
    update,
    setEditMode,
}: {
    form: WarehouseFormState;
    setForm: React.Dispatch<React.SetStateAction<WarehouseFormState>>;
    update?: boolean;
    setEditMode?: React.Dispatch<React.SetStateAction<boolean>>;
}) {
    const navigate = useNavigate();
    const [showPostcode, setShowPostcode] = useState(false);
    const { mutate: createWarehouse, isPending } = usePostConsoleWarehouse();
    const { mutate: updateWarehouse, isPending: isPendingUpdate } = usePutConsoleWarehouse();
    // 기본 담당기사 셀렉트 옵션 (기사 관리 전체)
    const { data: driverData } = useGetConsoleDrivers({ limit: 1000, page: 1 });

    // 주소 검색 완료 (받을 주소지 컴포넌트 재사용)
    const handleAddress = (data: any) => {
        let fullAddress = data.address;
        if (data.addressType === "R") {
            if (data.bname) fullAddress += ` ${data.bname}`;
            if (data.buildingName) fullAddress += `, ${data.buildingName}`;
        }
        setForm(prev => ({ ...prev, post: data.zonecode, address: fullAddress }));
        setShowPostcode(false);
    };

    const onSubmit = (e: FormEvent) => {
        e.preventDefault();
        if (!form.name.trim()) return toast.error("입고지명을 입력해주세요.", toastErrorSyle);

        const body = {
            port_name: form.port_name,
            name: form.name,
            driver_no: form.driver_no,
            post: form.post,
            address1: form.address,
            address2: form.addressDetail,
        };

        if (update && form.warehouse_no) {
            updateWarehouse(
                { ...body, warehouse_no: String(form.warehouse_no) },
                {
                    onSuccess: () => {
                        toast.success("성공적으로 수정 되었습니다.");
                        setEditMode?.(false);
                    },
                    onError: (error: any) => toast.error(error.message, toastErrorSyle),
                },
            );
            return;
        }

        createWarehouse(body, {
            onSuccess: (data: any) => {
                toast.success("성공적으로 등록 되었습니다.");
                const newNo = data?.data?.warehouse_no;
                navigate(newNo ? `/morning/customer/warehouse/${newNo}` : "/morning/customer/warehouse");
            },
            onError: (error: any) => toast.error(error.message, toastErrorSyle),
        });
    };

    const inputStyle = "border border-[#E1E2E4] rounded-[5px] px-3 h-[44px] text-[16px] w-full max-w-[400px]";
    // 주소 블록 내부 인풋(컨테이너가 max-w 제한) — 동일 높이
    const addrInputStyle = "border border-[#E1E2E4] rounded-[5px] px-3 h-[44px] text-[16px] w-full bg-white";

    return (
        <>
            {isPending || isPendingUpdate ? (
                <LoadingPop />
            ) : (
                <form onSubmit={onSubmit} className="h-full flex w-full">
                    <div className="mt-[8px] mb-[40px] flex-1 flex flex-col">
                        <ConsoleHrozontalHeadTable
                            thead={[
                                {
                                    title: "항구명",
                                    key: "port_name",
                                    content: () => (
                                        <input
                                            type="text"
                                            value={form.port_name}
                                            onChange={e => setForm(prev => ({ ...prev, port_name: e.target.value }))}
                                            placeholder="항구명을 입력해주세요."
                                            className={inputStyle}
                                        />
                                    ),
                                },
                                {
                                    title: "입고지명",
                                    key: "name",
                                    content: () => (
                                        <input
                                            type="text"
                                            value={form.name}
                                            onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                                            placeholder="입고지명을 입력해주세요."
                                            className={inputStyle}
                                        />
                                    ),
                                },
                                {
                                    title: "담당기사",
                                    key: "driver_no",
                                    content: () => (
                                        <Select
                                            value={form.driver_no}
                                            onValueChange={value => setForm(prev => ({ ...prev, driver_no: value }))}
                                        >
                                            <SelectTrigger className="relative shadow-none text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2 w-full max-w-[400px] border border-[#E1E2E4] bg-white h-[44px] px-3">
                                                <SelectValue placeholder="담당기사를 선택해주세요." />
                                                <ArrowDown className="[&_path]:fill-[#666666]" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white">
                                                {driverData?.data_list.map(d => (
                                                    <SelectItem key={d.driver_no} value={String(d.driver_no)}>
                                                        {d.name} ({d.phone})
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ),
                                },
                                {
                                    title: "주소",
                                    key: "post",
                                    content: () => (
                                        <div className="flex flex-col gap-1.5 relative max-w-[400px]">
                                            <div className="flex gap-1.5">
                                                <input
                                                    type="text"
                                                    disabled
                                                    value={form.post}
                                                    onClick={() => setShowPostcode(true)}
                                                    placeholder="우편번호"
                                                    className={`${addrInputStyle} cursor-pointer`}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPostcode(true)}
                                                    className="min-w-[90px] shrink-0 border border-[#1B1C1E] rounded-[5px] px-3 h-[44px] text-[16px] whitespace-nowrap"
                                                >
                                                    우편번호
                                                </button>
                                            </div>
                                            <input
                                                type="text"
                                                disabled
                                                value={form.address}
                                                onClick={() => setShowPostcode(true)}
                                                placeholder="주소"
                                                className={`${addrInputStyle} cursor-pointer`}
                                            />
                                            <input
                                                type="text"
                                                value={form.addressDetail}
                                                onChange={e => setForm(prev => ({ ...prev, addressDetail: e.target.value }))}
                                                placeholder="상세주소 입력"
                                                className={addrInputStyle}
                                            />
                                            {showPostcode && (
                                                <div className="absolute z-[100] mt-[34px] top-[22px] bg-white border w-full rounded-lg shadow-lg overflow-hidden">
                                                    <div className="px-4 py-3 font-bold border-b border-[#dddddd] flex items-center justify-between">
                                                        <p>우편번호 찾기</p>
                                                        <Close onClick={() => setShowPostcode(false)} className="cursor-pointer" />
                                                    </div>
                                                    <DaumPostcodeEmbed className="min-h-[470px]" onComplete={handleAddress} />
                                                </div>
                                            )}
                                        </div>
                                    ),
                                },
                            ]}
                            tbody={form}
                            className="overflow-visible"
                        />

                        <div className="flex justify-center gap-2 mt-5">
                            <button className="bg-black text-white rounded-[5px] px-4 py-2">저장</button>
                            <button
                                type="button"
                                className="bg-black text-white rounded-[5px] px-4 py-2"
                                onClick={() => (update && setEditMode ? setEditMode(false) : navigate(-1))}
                            >
                                {update ? "취소" : "목록"}
                            </button>
                        </div>
                    </div>
                </form>
            )}
            <Toaster position="bottom-center" reverseOrder={false} />
        </>
    );
}
